import {Injectable, Injector, PLATFORM_ID} from '@angular/core';
import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpErrorResponse
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, EMPTY, from } from 'rxjs';
import {
    catchError,
    filter,
    switchMap,
    take,
    finalize
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '@iw-shop/store/state/app.state';
import { setNotification } from '@iw-shop/store/actions/global.actions';
import { logout, refreshTokenSuccess } from '@iw-shop/store/actions/authentication.action';
import { AuthenticationService } from '@iw-shop/core/services/authentication.service';
import {Router} from "@angular/router";
import {isPlatformBrowser} from "@angular/common";

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private disableRequests = false;

    constructor(
        private injector: Injector,
        private store: Store<AppState>,
        private router: Router
    ) {}

    // Hilfsmethode, um den Service erst bei Bedarf zu holen
    private get authenticationService(): AuthenticationService {
        return this.injector.get(AuthenticationService);
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const platformId = this.injector.get(PLATFORM_ID);

        if (!isPlatformBrowser(platformId)) {
            return next.handle(request); // ✅ SSR safe
        }

        if (this.disableRequests) return EMPTY;

        // 1. Initialer Check: Muss der Token erneuert werden?
        const isExpired = this.authenticationService.isAccessTokenExpired(30);
        const hasToken = (
            this.authenticationService.isAccessToken() &&
            this.authenticationService.isAccessTokenCookie() &&
            !this.authenticationService.isRefreshTokenExpired()
        );

        if (hasToken && isExpired) {
            return this.handleTokenRefresh(request, next);
        }

        // 2. Normalen Request senden (mit injiziertem Token falls nötig)
        return next.handle(this.injectToken(request)).pipe(
            catchError((error) => {
                // Falls der Server trotzdem 401 liefert, obwohl wir dachten der Token sei okay
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    return this.handleTokenRefresh(request, next);
                }
                return throwError(() => error);
            })
        );
    }

    private handleTokenRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(false);

            return this.authenticationService.refreshToken().pipe(
                switchMap((refreshResponse: any) => {
                    if (refreshResponse?.result) {

                        // Erfolg: Token-Daten extrahieren (je nach Endpoint Struktur)
                        this.store.dispatch(refreshTokenSuccess({ accessExpiredIn: refreshResponse.result?.userTokens.data.expiredIn }));

                        this.isRefreshing = false;
                        this.refreshTokenSubject.next(true);
                        return next.handle(this.injectToken(request));
                    } else {
                        this.handleInvalidSession();
                        return EMPTY;
                    }
                }),
                catchError((err) => {
                    this.handleInvalidSession();
                    return throwError(() => err);
                }),
                finalize(() => {
                    this.isRefreshing = false;
                })
            );
        } else {
            // Warten, bis der erste Request den Refresh fertig hat
            return this.refreshTokenSubject.pipe(
                filter(success => success),
                take(1),
                switchMap(() => next.handle(this.injectToken(request)))
            );
        }
    }

    // Hilfsfunktion, um den Header zu setzen (nutzt deine Service-Logik)
    injectToken(request: HttpRequest<any>): HttpRequest<any> {
        if (this.authenticationService.isInjectionToken()) {
            const injectToken = this.authenticationService.getInjectionToken();
            if (injectToken) {
                return request.clone({
                    setHeaders: { Authorization: `Bearer ${injectToken}` }
                });
            }
        }
        // Falls dein Service den Access-Token über Cookies regelt (HttpClient schickt diese autom. mit),
        // musst du hier nichts weiter clonen.
        // Falls er im Header stehen muss, bräuchtest du eine Methode wie getAccessToken().
        return request;
    }

    private handleInvalidSession() {
        this.disableRequests = true;
        this.store.dispatch(logout());
        this.store.dispatch(setNotification({
            notification: 'Ihre Session ist abgelaufen. Bitte loggen Sie sich erneut ein.',
            notificationType: 'info'
        }));
        const platformId = this.injector.get(PLATFORM_ID);
// 3. Navigation statt Reload
        // Wir navigieren zum Login. Das löscht den State der Komponenten automatisch.
        if (isPlatformBrowser(platformId)) {
            this.router.navigate(['/']).then(() => {
                this.disableRequests = false;
            });
        }
    }
}

// Helper um die verschachtelten Pfade sauber zu lesen
